import React from 'react'

/**
 * Ai照片君 扁平图标库。
 *
 * 全部 16x16 viewBox,stroke 走 currentColor,父容器靠 color 控色,size 默认 14。
 * 替代旧版 emoji(🤖 💡 💬 🔎 💭 ⏳ 📋 🥇🥈🥉 ✨),避免 Apple Color Emoji
 * 的 3D 拟物风跟整窗的暖现代毛玻璃割裂。
 *
 * 不依赖 antd Icon —— antd 3.x 的 IconFont 风格偏 Material,跟新调色板不搭。
 */

interface IconProps {
  size?: number
  className?: string
}

const baseProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
}

export const IconLightbulb: React.FC<IconProps> = ({
  size = 14,
  className
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <path d="M5 6.5a3 3 0 1 1 6 0c0 1.2-.5 2-1.2 2.7-.4.4-.8.7-.8 1.3v1.5h-2v-1.5c0-.6-.4-.9-.8-1.3C5.5 8.5 5 7.7 5 6.5z" />
    <path d="M7 13h2" />
    <path d="M7.3 14.5h1.4" />
  </svg>
)

export const IconChat: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <path d="M2.5 4.5a1.5 1.5 0 0 1 1.5-1.5h8a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H6L3.5 13.5V11H4a1.5 1.5 0 0 1-1.5-1.5z" />
  </svg>
)

export const IconSearch: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <circle cx="7" cy="7" r="4" />
    <path d="M10 10l3.5 3.5" />
  </svg>
)

export const IconClipboard: React.FC<IconProps> = ({
  size = 14,
  className
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <rect x="3.5" y="3.5" width="9" height="10" rx="1.2" />
    <path d="M6 2.5h4v2H6z" />
    <path d="M5.8 7.5h4.4M5.8 10h4.4M5.8 12.2h2.7" />
  </svg>
)

export const IconSparkle: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    fill="currentColor"
  >
    <path d="M8 1.5l1.4 4.1L13.5 7l-4.1 1.4L8 12.5l-1.4-4.1L2.5 7l4.1-1.4z" />
  </svg>
)

/** AI 入口星标:橙黄渐变四角星 + 发光阴影(设计稿 Frame 1) */
export const IconAiStar: React.FC<IconProps> = ({ size = 24, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#aiStarShadow)">
      <path
        d="M22.6697 8.58211C23.0267 7.80604 24.1296 7.80604 24.4866 8.58211L25.8342 11.5114C27.829 15.8474 31.3087 19.3271 35.6447 21.3219L38.574 22.6695C39.3501 23.0265 39.3501 24.1294 38.574 24.4865L35.6447 25.8341C31.3087 27.8289 27.829 31.3086 25.8342 35.6445L24.4866 38.5739C24.1296 39.3499 23.0267 39.3499 22.6697 38.5739L21.322 35.6445C19.3272 31.3086 15.8475 27.8289 11.5116 25.8341L8.58225 24.4865C7.80618 24.1294 7.80618 23.0265 8.58225 22.6695L11.5116 21.3219C15.8475 19.3271 19.3272 15.8474 21.322 11.5114L22.6697 8.58211Z"
        fill="url(#aiStarGrad)"
      />
    </g>
    <defs>
      <filter
        id="aiStarShadow"
        x="0"
        y="0"
        width="47.1562"
        height="47.156"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="4" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 1 0 0 0 0 0.783333 0 0 0 0 0 0 0 0 0.25 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        />
      </filter>
      <linearGradient
        id="aiStarGrad"
        x1="36.0723"
        y1="31.0718"
        x2="7.07227"
        y2="17.0718"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E9AD50" />
        <stop offset="1" stopColor="#FECC4F" />
      </linearGradient>
    </defs>
  </svg>
)

/** banner 前缀:紫色放大镜(设计稿) */
export const IconSearchDoc: React.FC<IconProps> = ({
  size = 16,
  className
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.0313 8.61729L13.7063 12.2923L12.2923 13.7063L8.61729 10.0313C7.48712 10.8088 6.10719 11.1359 4.74828 10.9484C3.38938 10.7609 2.14958 10.0724 1.27217 9.01787C0.39476 7.96339 -0.0569238 6.61908 0.00574728 5.24873C0.0684184 3.87838 0.64092 2.58091 1.61092 1.61092C2.58091 0.64092 3.87838 0.0684184 5.24873 0.00574728C6.61908 -0.0569238 7.96339 0.39476 9.01787 1.27217C10.0724 2.14958 10.7609 3.38938 10.9484 4.74828C11.1359 6.10719 10.8088 7.48712 10.0313 8.61729ZM5.49929 8.99929C5.95892 8.99929 6.41404 8.90876 6.83868 8.73287C7.26332 8.55698 7.64916 8.29917 7.97416 7.97416C8.29917 7.64916 8.55698 7.26332 8.73287 6.83868C8.90876 6.41404 8.99929 5.95892 8.99929 5.49929C8.99929 5.03966 8.90876 4.58454 8.73287 4.1599C8.55698 3.73526 8.29917 3.34942 7.97416 3.02442C7.64916 2.69941 7.26332 2.4416 6.83868 2.26571C6.41404 2.08982 5.95892 1.99929 5.49929 1.99929C4.57103 1.99929 3.68079 2.36804 3.02442 3.02442C2.36804 3.68079 1.99929 4.57103 1.99929 5.49929C1.99929 6.42755 2.36804 7.31778 3.02442 7.97416C3.68079 8.63054 4.57103 8.99929 5.49929 8.99929Z"
      fill="#6B48DF"
    />
  </svg>
)

/** 思考流式预览前缀。简洁的"小气泡 + 三点"。 */
export const IconThinking: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <path d="M2.5 5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v3.5a2 2 0 0 1-2 2H6L3 13v-2.5a2 2 0 0 1-.5-1.5z" />
    <circle cx="6" cy="6.8" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="8" cy="6.8" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="10" cy="6.8" r="0.6" fill="currentColor" stroke="none" />
  </svg>
)

/** 进度运行中的脉冲圆点,替代 ⏳。 */
export const IconRunningDot: React.FC<IconProps> = ({
  size = 10,
  className
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 10 10"
    className={className}
    fill="currentColor"
  >
    <circle cx="5" cy="5" r="3">
      <animate
        attributeName="opacity"
        values="1;0.35;1"
        dur="1.4s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
)

/** 沙漏 / 等待,替代 ⏳ 在下载占位场景。 */
export const IconHourglass: React.FC<IconProps> = ({
  size = 18,
  className
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...baseProps}
  >
    <path d="M5 2.5h6M5 13.5h6" />
    <path d="M5.5 2.5v2.2c0 1 .5 1.6 1.2 2.2L8 8l1.3-1.1c.7-.6 1.2-1.2 1.2-2.2V2.5" />
    <path d="M5.5 13.5v-2.2c0-1 .5-1.6 1.2-2.2L8 8l1.3 1.1c.7.6 1.2 1.2 1.2 2.2v2.2" />
  </svg>
)

/** 点赞: 实心 = 已选, 描线 = 未选。 */
export const IconThumbUp: React.FC<IconProps & { filled?: boolean }> = ({
  size = 14,
  className,
  filled
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...(filled
      ? { fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1 }
      : baseProps)}
  >
    <path d="M5.5 7L8 2.5c.4-.7 1.4-.5 1.4.4V6h3.1c.7 0 1.2.6 1 1.3l-1 4.5c-.1.6-.7 1-1.3 1H5.5z" />
    <path d="M5.5 7v6.8H3.8c-.4 0-.8-.4-.8-.8V7.8c0-.4.4-.8.8-.8z" />
  </svg>
)

/** 点踩: 实心 = 已选, 描线 = 未选。 */
export const IconThumbDown: React.FC<IconProps & { filled?: boolean }> = ({
  size = 14,
  className,
  filled
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    className={className}
    {...(filled
      ? { fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1 }
      : baseProps)}
  >
    <path d="M10.5 9L8 13.5c-.4.7-1.4.5-1.4-.4V10H3.5c-.7 0-1.2-.6-1-1.3l1-4.5c.1-.6.7-1 1.3-1h5.7z" />
    <path d="M10.5 9V2.2h1.7c.4 0 .8.4.8.8v5.2c0 .4-.4.8-.8.8z" />
  </svg>
)

/** 排名奖牌:扁平圆 + 缎带,数字嵌中。仅 1/2/3 名。 */
export const MedalIcon: React.FC<{ rank: 1 | 2 | 3; size?: number }> = ({
  rank,
  size = 22
}) => {
  const palette = {
    1: { ring: '#e9b341', fill: '#fff1cf', text: '#7a5311' },
    2: { ring: '#a4abb4', fill: '#eef0f3', text: '#4f5560' },
    3: { ring: '#c98a6a', fill: '#fadfd0', text: '#6b3a23' }
  }[rank]
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      {/* 缎带,左右两条 */}
      <path d="M7.5 2L9.5 9.5h-3L4.5 4z" fill={palette.ring} opacity="0.85" />
      <path d="M16.5 2l-2 7.5h3L19.5 4z" fill={palette.ring} opacity="0.85" />
      {/* 主奖章 */}
      <circle
        cx="12"
        cy="15"
        r="6.5"
        fill={palette.fill}
        stroke={palette.ring}
        strokeWidth="1.4"
      />
      <text
        x="12"
        y="17.6"
        textAnchor="middle"
        fontSize="8"
        fontWeight={700}
        fill={palette.text}
        fontFamily="-apple-system, system-ui, sans-serif"
      >
        {rank}
      </text>
    </svg>
  )
}

// 历史会话:时钟+回旋箭头,fill 类(不走 baseProps 的 stroke)。
// 默认 width/height: 1em,跟随父级 font-size。viewBox 收紧到 path 实际边界
// (106-918),裁掉无意义留白,视觉占比与 antd icon 对齐。
// 显式传 size 仍按像素生效。
export const IconHistory: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="90 90 844 844"
    className={className}
    fill="currentColor"
  >
    <path d="M512 106.688A405.376 405.376 0 1 1 106.688 512a32 32 0 0 1 64 0 341.376 341.376 0 1 0 79.36-218.624h58.304l6.464 0.64a32 32 0 0 1 0 62.72l-6.4 0.64H172.544a32 32 0 0 1-32-32V189.632a32 32 0 0 1 64 0v58.24A404.416 404.416 0 0 1 512 106.688zM512.128 256a32 32 0 0 1 32 32l-0.064 210.88 148.928 148.928a32.064 32.064 0 0 1-45.248 45.312L489.408 534.784a32.128 32.128 0 0 1-9.344-22.592V288a32 32 0 0 1 32.064-32z" />
  </svg>
)

// 新会话:对话气泡 + 内嵌 +,fill 类。
// 注意: path 自身是横向矩形(1000 宽 × 889 高),不是正方形。
// 用 width=1.125em height=1em 让 SVG 元素按 path 真实宽高比渲染,
// 高度跟 1em-square icon 视觉一致,宽度按聊天气泡天然形状略宽,不出现 letterbox。
// 显式传 size 时仍按方形像素生效(此时会有 letterbox,适合不在乎严格一致的场景)。
export const IconNewChat: React.FC<IconProps> = ({ size, className }) =>
  size != null ? (
    <svg
      width={size}
      height={size}
      viewBox="0 73 1000 889"
      className={className}
      fill="currentColor"
    >
      <path d="M633.856 73.152a365.184 365.184 0 0 1 365.76 365.056v0.64c0 186.432-164.16 352.32-492.48 497.664-19.2 8.448-38.912 16.896-59.136 25.216a24.32 24.32 0 0 1-33.472-22.4v-134.784h-48.832a365.696 365.696 0 0 1 0-731.392h268.16zM365.696 165.76c-4.736 0-9.408 0.064-14.016 0.32a259.328 259.328 0 0 0-158.528 60.928c-7.04 5.76-13.888 12.096-20.48 18.752-26.688 26.624-46.72 56.064-60.032 88.256a253.44 253.44 0 0 0-15.04 50.304 278.08 278.08 0 0 0-4.992 54.464 311.488 311.488 0 0 0 1.28 27.776 278.272 278.272 0 0 0 9.984 52.48 253.312 253.312 0 0 0 41.6 81.728 296.64 296.64 0 0 0 47.68 49.856 273.92 273.92 0 0 0 44.096 30.016c38.528 20.8 81.28 31.232 128.448 31.232h141.44v122.88c49.92-23.168 95.232-46.656 135.872-70.4a942.08 942.08 0 0 0 123.008-84.48c9.856-8.128 19.2-16.32 28.16-24.576 8.768-8.192 17.152-16.448 24.96-24.704 7.808-8.32 15.104-16.64 21.888-24.96 43.968-54.272 65.92-109.888 65.92-166.848v-0.64c0-75.328-26.624-139.584-79.872-192.704-49.92-49.792-109.696-76.288-179.136-79.36a314.112 314.112 0 0 0-14.08-0.32h-268.16z m195.072 224.32h121.92v97.472H560.768v121.92H463.232V487.68H341.312V390.144h121.92V268.16h97.536v121.984z" />
    </svg>
  ) : (
    <svg
      width="1.125em"
      height="1em"
      viewBox="0 73 1000 889"
      className={className}
      fill="currentColor"
    >
      <path d="M633.856 73.152a365.184 365.184 0 0 1 365.76 365.056v0.64c0 186.432-164.16 352.32-492.48 497.664-19.2 8.448-38.912 16.896-59.136 25.216a24.32 24.32 0 0 1-33.472-22.4v-134.784h-48.832a365.696 365.696 0 0 1 0-731.392h268.16zM365.696 165.76c-4.736 0-9.408 0.064-14.016 0.32a259.328 259.328 0 0 0-158.528 60.928c-7.04 5.76-13.888 12.096-20.48 18.752-26.688 26.624-46.72 56.064-60.032 88.256a253.44 253.44 0 0 0-15.04 50.304 278.08 278.08 0 0 0-4.992 54.464 311.488 311.488 0 0 0 1.28 27.776 278.272 278.272 0 0 0 9.984 52.48 253.312 253.312 0 0 0 41.6 81.728 296.64 296.64 0 0 0 47.68 49.856 273.92 273.92 0 0 0 44.096 30.016c38.528 20.8 81.28 31.232 128.448 31.232h141.44v122.88c49.92-23.168 95.232-46.656 135.872-70.4a942.08 942.08 0 0 0 123.008-84.48c9.856-8.128 19.2-16.32 28.16-24.576 8.768-8.192 17.152-16.448 24.96-24.704 7.808-8.32 15.104-16.64 21.888-24.96 43.968-54.272 65.92-109.888 65.92-166.848v-0.64c0-75.328-26.624-139.584-79.872-192.704-49.92-49.792-109.696-76.288-179.136-79.36a314.112 314.112 0 0 0-14.08-0.32h-268.16z m195.072 224.32h121.92v97.472H560.768v121.92H463.232V487.68H341.312V390.144h121.92V268.16h97.536v121.984z" />
    </svg>
  )

export const IconMore: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1.2em'}
    height={size ?? '1.2em'}
    viewBox="0 0 1024 1024"
    className={className}
    fill="currentColor"
  >
    <path d="M256 512m-74.666667 0a74.666667 74.666667 0 1 0 149.333334 0 74.666667 74.666667 0 1 0-149.333334 0Z" />
    <path d="M512 512m-74.666667 0a74.666667 74.666667 0 1 0 149.333334 0 74.666667 74.666667 0 1 0-149.333334 0Z" />
    <path d="M768 512m-74.666667 0a74.666667 74.666667 0 1 0 149.333334 0 74.666667 74.666667 0 1 0-149.333334 0Z" />
  </svg>
)

export const IconWechat: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 1024 1024"
    className={className}
  >
    <path
      d="M1024 619.52c0-143.36-138.24-256-307.2-256s-307.2 112.64-307.2 256 138.24 256 307.2 256c30.72 0 61.44-5.12 92.16-10.24l97.28 51.2-25.6-76.8c87.04-51.2 143.36-128 143.36-220.16z m-414.72-40.96c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z m209.92 0c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z"
      fill="#4CBF00"
    />
    <path
      d="M358.4 609.28c0-158.72 153.6-286.72 348.16-286.72h15.36c-40.96-133.12-179.2-235.52-353.28-235.52-204.8 0-368.64 138.24-368.64 307.2 0 107.52 66.56 204.8 168.96 256l-30.72 92.16L256 686.08c35.84 10.24 71.68 15.36 112.64 15.36h10.24c-15.36-30.72-20.48-61.44-20.48-92.16z m138.24-414.72c35.84 0 66.56 30.72 66.56 66.56s-30.72 66.56-66.56 66.56C460.8 322.56 430.08 291.84 430.08 256S460.8 194.56 496.64 194.56zM245.76 322.56c-35.84 0-61.44-30.72-61.44-66.56s30.72-66.56 66.56-66.56 61.44 30.72 61.44 66.56-30.72 66.56-66.56 66.56z"
      fill="#4CBF00"
    />
  </svg>
)

export const IconPhone: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 1024 1024"
    className={className}
  >
    <path
      d="M926.342144 849.004544c34.132992-59.40224 15.22688-105.025536-45.21984-150.674432-71.057408-53.664768-140.500992-89.761792-190.43328-31.939584 0 0-52.989952 63.081472-208.927744-84.535296-181.32992-172.8512-105.227264-234.155008-105.227264-234.155008 63.065088-63.401984 22.976512-110.700544-29.915136-182.334464-52.887552-71.632896-106.313728-94.30528-183.013376-32.697344C15.804416 251.394048 224.212992 530.164736 329.58464 638.56128l-0.004096 0.008192c0 0 160.234496 165.740544 261.066752 220.915712l53.941248 30.16192c77.366272 39.701504 164.267008 57.875456 225.446912 21.009408C870.035456 910.65856 899.526656 895.489024 926.342144 849.004544z"
      fill="#FF9000"
    />
  </svg>
)

export const IconClock: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 1024 1024"
    className={className}
  >
    <path
      d="M498.206118 120.470588c208.594824 0 377.705412 169.110588 377.705411 377.73553 0 208.594824-169.110588 377.705412-377.705411 377.705411C289.581176 875.911529 120.470588 706.800941 120.470588 498.206118 120.470588 289.581176 289.581176 120.470588 498.206118 120.470588z m0.150588 140.559059a26.352941 26.352941 0 0 0-26.352941 26.352941l-0.030118 210.974118 0.210824 3.463529c0.783059 5.722353 3.373176 11.053176 7.529411 15.179294l148.931765 148.961883 2.710588 2.349176a26.352941 26.352941 0 0 0 34.575059-2.349176l2.349177-2.710588a26.352941 26.352941 0 0 0-2.349177-34.575059l-141.281882-141.251765 0.060235-200.041412-0.210823-3.312941a26.352941 26.352941 0 0 0-26.142118-23.04z"
      fill="#1296db"
    />
  </svg>
)

/* ── 面板头部按钮 v2(2026-06-11 设计稿,20 viewBox 描边 1.67,颜色随 currentColor)── */

/** 头部:历史会话(逆时针时钟) */
export const IconHistoryLine: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 20 20"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.66667}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.42578 2.80298V5.83327H5.45609" />
    <path d="M1.66602 10.0001C1.66602 14.6025 5.39697 18.3334 9.99935 18.3334C14.6017 18.3334 18.3327 14.6025 18.3327 10.0001C18.3327 5.39771 14.6017 1.66675 9.99935 1.66675C6.91518 1.66675 4.22231 3.34221 2.78134 5.83262" />
    <path d="M10.0025 5L10.002 10.0037L13.535 13.5368" />
  </svg>
)

/** 头部:开启新会话(气泡 + 加号) */
export const IconNewChatLine: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 20 20"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.66667}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.99935 18.3334C14.6017 18.3334 18.3327 14.6025 18.3327 10.0001C18.3327 5.39771 14.6017 1.66675 9.99935 1.66675C5.39697 1.66675 1.66602 5.39771 1.66602 10.0001C1.66602 11.13 1.89088 12.2073 2.29829 13.1898C2.50865 13.6971 2.76767 14.1791 3.06953 14.63C3.17084 14.7814 2.98078 15.738 2.49935 17.5001C4.26139 17.0187 5.21806 16.8286 5.36939 16.9299C5.82031 17.2317 6.30231 17.4908 6.8096 17.7011C7.7921 18.1085 8.86947 18.3334 9.99935 18.3334Z" />
    <path d="M6 10H14" />
    <path d="M9.99981 6.09995L10.0048 13.8753" />
  </svg>
)

/** 头部:下载任务(托盘向下箭头);有任务时由 CSS 置蓝 */
export const IconDownloadLine: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 20 20"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.66667}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.5 10.0035V17.5H17.5V10" />
    <path d="M13.75 9.58325L10 13.3333L6.25 9.58325" />
    <path d="M9.99609 2.5V13.3333" />
  </svg>
)

/** 头部:关闭(斜叉) */
export const IconCloseLine: React.FC<IconProps> = ({ size, className }) => (
  <svg
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 20 20"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.66667}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.33398 3.33325L16.6673 16.6666" />
    <path d="M3.33398 16.6666L16.6673 3.33325" />
  </svg>
)
